import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Grid,
  Button,
  Select,
  Divider,
  TextField,
  InputLabel,
  Typography,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import dayjs from "dayjs";

import KPTimePicker from "../../../components/KPTimePicker";
import toJs from "../../../hoc/toJS";
import select from "../../../utils/select";

import MultiFileManager from "../../File/MultiFileManager/MultiFileManager";

import { uploadMultiFile } from "../../../reducer/file/action";
import {
  insertScientificCouncil,
  updateScientificCouncil,
  checkDuplicateScientificCouncil,
} from "../../../reducer/scientificCouncilNew/action";
import { statisticUnaccepted } from "../../../reducer/profile/action";
import { notification } from "../../../reducer/home/action";

import { getCurrentSchoolYear } from "../../User/TimeResearch/utils";
import { SCIENTIFIC_COUNCIL_NEW } from "../../../constants/selectOptions";
import DuplicateCheckModal from "../../../components/ScientificCouncilNew/DuplicateCheckModal";

function ScientificCouncilInsertModal(props) {
  const { inserting, setInserting } = props;

  const currentYear = getCurrentSchoolYear();
  const [values, setValues] = useState({
    purposeId: 5,
    departmentId: props.profileDepartments?.[0]?.id || null,
  });
  const [profileList, setProfileList] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [timeResearchYear, setTimeResearchYear] = useState(currentYear);
  const [timeResearchDetailList, setTimeResearchDetailList] = useState([]);
  const [helperText, setHelperText] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateList, setDuplicateList] = useState([]);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const profileListFromProps = props.profileList;

  // Tự gán bản thân vào danh sách thành viên
  React.useEffect(() => {
    if (props.profile?.id && profileList.length === 0) {
      setProfileList([
        {
          profileId: props.profile.id,
          roleInScientificCouncil: "chairman",
          isInternal: true,
          externalName: "",
        },
      ]);
      // Set initial role to values for timeResearchDetailCode calculation
      setValues((prev) => ({ ...prev, role: "chairman" }));
    }
  }, [inserting, props.profile?.id]);

  useEffect(() => {
    if (props.timeResearchInformationList?.length > 0 && timeResearchYear) {
      let list = props.timeResearchInformationList
        ?.find((e) => e.year === 0)
        ?.timeResearchDetail.filter((e) => e.type === "scientificCouncil");
      setTimeResearchDetailList(list || []);

      // Don't reset timeResearchDetailCode here, let the next useEffect handle it
    }
  }, [props.timeResearchInformationList, timeResearchYear]);

  // Auto-calculate timeResearchDetailCode based on type and role
  useEffect(() => {
    if (values?.type && values?.role && timeResearchDetailList.length > 0) {
      const mapCode = `${values.type}_${values.role}`;
      const matchedOption = timeResearchDetailList.find((option) => option.mapCode === mapCode);
      if (matchedOption) {
        setValues((prevValues) => ({
          ...prevValues,
          timeResearchDetailCode: matchedOption.code,
        }));
      }
    }
  }, [values?.type, values?.role, timeResearchDetailList]);

  const handleChangeForm = (name, value) => {
    if (name === "timeResearchDetailCode") {
      setValues({
        ...values,
        [name]: value === "null" ? null : value,
      });
    } else if (name === "executionTime" && value) {
      // Auto-calculate year from executionTime
      if (value !== "dd/mm/yyyy") {
        const parts = value.split("/");
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]);
        const year = parseInt(parts[2]);

        // Calculate academic year: months 1-3 use previous year, months 4-12 use current year
        const calculatedYear = month >= 1 && month <= 3 ? year - 1 : year;

        setTimeResearchYear(calculatedYear);
        setValues({
          ...values,
          [name]: value,
          year: calculatedYear,
        });
        return;
      }
    } else {
      setValues({ ...values, [name]: value });
    }
  };

  const handleChangeProfileList = (index, name, value) => {
    const temp = profileList;
    const tempElm = profileList[index];
    temp.splice(index, 1, { ...tempElm, [name]: value });
    setProfileList([...temp]);

    // If changing role of first member (current user), update values.role for timeResearchDetailCode calculation
    if (index === 0 && name === "roleInScientificCouncil") {
      setValues((prevValues) => ({
        ...prevValues,
        role: value,
      }));
    }
  };

  const handleInsert = () => {
    const temp = profileList;
    temp.push({
      profileId: null,
      roleInScientificCouncil: "commissioner",
      isInternal: true,
      externalName: "",
    });
    setProfileList([...temp]);
  };

  const handleRemove = (index) => {
    const temp = profileList;
    temp.splice(index, 1);
    setProfileList([...temp]);
  };

  // Convert dd/mm/yyyy to YYYY-MM-DDTHH:mm:ssZ for backend
  const convertDateToISO = (value) => {
    if (!value || value === "dd/mm/yyyy") return null;
    const dateForm = value.replaceAll("/", "");
    const date = dateForm.substr(0, 2);
    const month = dateForm.substr(2, 2);
    const year = dateForm.substr(-4);
    return dayjs(`${month}/${date}/${year}`).format("YYYY-MM-DDTHH:mm:ss") + "Z";
  };

  const insertScientificCouncilItem = async () => {
    let result = await props
      .insertScientificCouncil({
        ...values,
        executionTime: convertDateToISO(values.executionTime),
        scientificCouncilProfile: profileList,
      })
      .then(async (response) => {
        if (response && newFiles.length > 0) {
          const uploadResult = await props.uploadMultiFile(response.id, newFiles, "scientificCouncil");
          if (uploadResult.success) {
            await props.updateScientificCouncil(response.id, {
              ...response,
              fileUrl: uploadResult.data.join("||"),
              scientificCouncilProfile: profileList,
            });
          }
        }
        if (response) {
          props.statisticUnaccepted({
            departmentId: 0,
            profileId: props.profile?.id,
          });
          if (props.role === "sudo-admin") props.statisticUnaccepted({ departmentId: 0, profileId: 0 });
        }
        return response;
      });
    return result;
  };

  const checkForDuplicates = async (title) => {
    if (!title || title.trim() === "") return false;

    const result = await props.checkDuplicateScientificCouncil({
      publicationType: "scientific_councils",
      title: title.trim(),
    });

    if (result.success && result.data && result.data.length > 0) {
      setDuplicateList(result.data);
      setDuplicateModalOpen(true);
      return true;
    }
    return false;
  };

  const handleSelectDuplicate = (item) => {
    setDuplicateModalOpen(false);
    setInserting(false);
    if (props.setUpdating) {
      props.setUpdating({
        ...item,
        scientificCouncilProfile: item.scientificCouncilProfile || [],
        _fromDuplicate: true,
      });
    }
  };

  const handleCloseDuplicateModal = () => {
    setDuplicateModalOpen(false);
  };

  const proceedWithSubmit = () => {
    insertScientificCouncilItem().then((success) => {
      if (success) {
        props.statisticUnaccepted({
          departmentId: 0,
          profileId: props.profile?.id,
        });
        if (props.role === "sudo-admin") props.statisticUnaccepted({ departmentId: 0, profileId: 0 });
      }
    });

    setValues({});
    setProfileList([]);
    setNewFiles([]);
    setInserting(false);
    setHelperText(false);
  };

  const handleCloseInsertModal = () => {
    setDuplicateModalOpen(false);
    setDuplicateList([]);
    setPendingSubmit(false);
    setValues({
      purposeId: 5,
      departmentId: props.profileDepartments?.[0]?.id || null,
    });
    setProfileList([]);
    setNewFiles([]);
    setHelperText(false);
    setInserting(false);
  };

  const handleSubmit = async (e) => {
    // Check required fields
    if (!values?.title || !values?.executionTime || !values?.type) {
      setHelperText(true);
      return;
    }

    // Check timeResearchDetailCode if purposeId is 5
    if (values?.purposeId === 5 && !values?.timeResearchDetailCode) {
      setHelperText(true);
      return;
    }

    // Validate profileList for external members
    const hasEmptyExternalName = profileList.some((e) => !e.isInternal && e.externalName === "");
    if (hasEmptyExternalName) {
      setHelperText(true);
      return;
    }

    // Validate at least one internal member
    const hasInternalMember = profileList.some((e) => e.isInternal === true);
    if (!hasInternalMember) {
      props.pushNoti({
        type: "warning",
        message: "Phải có ít nhất 1 thành viên nội bộ của trường trong công trình khoa học!",
      });
      return;
    }

    // Check for duplicates
    const hasDuplicates = await checkForDuplicates(values?.title);
    if (hasDuplicates) {
      return;
    }

    insertScientificCouncilItem().then((success) => {
      if (success) {
        props.statisticUnaccepted({
          departmentId: 0,
          profileId: props.profile?.id,
        });
        if (props.role === "sudo-admin") props.statisticUnaccepted({ departmentId: 0, profileId: 0 });
      }
    });

    setValues({});
    setProfileList([]);
    setNewFiles([]);
    setInserting(false);
    setHelperText(false);
  };

  return (
    <Dialog fullWidth keepMounted maxWidth="lg" open={inserting} onClose={handleCloseInsertModal} disableBackdropClick>
      <DialogTitle>Thêm hội đồng khoa học</DialogTitle>
      <DialogContent style={{ display: "flex", gap: 16, overflowX: "hidden" }}>
        <div style={{ flex: "1 1 auto", minWidth: 0, paddingRight: 16 }}>
          <Grid container justifyContent="flex-start" spacing={2} style={{ marginBottom: 10 }}>
            <Grid item xs={10}>
              <TextField
                fullWidth
                size="small"
                label="Tên hội đồng"
                variant="outlined"
                value={values?.title?.toString() || ""}
                required
                error={helperText && !values?.title}
                helperText={helperText && !values?.title ? "Vui lòng nhập tên hội đồng" : ""}
                onChange={(event) => handleChangeForm("title", event.target.value)}
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Mã CTKH"
                value={values?.hemisCode || ""}
                onChange={(event) => handleChangeForm("hemisCode", event.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <KPTimePicker
                required
                fullWidth
                size="small"
                label="Thời gian thực hiện"
                value={values?.executionTime || "dd/mm/yyyy"}
                setValue={(newValue) => handleChangeForm("executionTime", newValue)}
              />
              {helperText && !values?.executionTime && (
                <FormHelperText error>Vui lòng chọn thời gian thực hiện</FormHelperText>
              )}
            </Grid>
            {/* <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Năm tính giờ"
                variant="outlined"
                value={values?.year ? `${values.year} - ${values.year + 1}` : ""}
                disabled
                InputProps={{
                  readOnly: true,
                }}
                helperText="Tự động tính từ thời gian thực hiện"
              />
            </Grid> */}
            <Grid item xs={6}>
              <Autocomplete
                fullWidth
                size="small"
                options={(props.departmentList || []).filter((d) => d.id !== 0)}
                getOptionLabel={(option) => option?.name || ""}
                value={
                  (props.departmentList || []).filter((d) => d.id !== 0).find((d) => d.id === values?.departmentId) ||
                  null
                }
                onChange={(event, newValue) => handleChangeForm("departmentId", newValue?.id)}
                renderInput={(params) => <TextField {...params} size="small" label="Đơn vị" variant="outlined" />}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                // label="Loại hội đồng"
                variant="outlined"
                value={values?.type || ""}
                required
                error={helperText && !values?.type}
                helperText={helperText && !values?.type ? "Vui lòng chọn loại hội đồng" : ""}
                onChange={(event) => handleChangeForm("type", event.target.value)}
              >
                <MenuItem value="">-- Chọn loại hội đồng --</MenuItem>
                {SCIENTIFIC_COUNCIL_NEW.councilType.map((type) => (
                  <MenuItem key={type.key} value={type.value}>
                    {type.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Nhiệm vụ tính giờ nghiên cứu"
                variant="outlined"
                value={
                  values?.timeResearchDetailCode && timeResearchDetailList.length > 0
                    ? (() => {
                        const detail = timeResearchDetailList.find((e) => e.code === values.timeResearchDetailCode);
                        return detail ? `${detail.description} - ${detail.value}(h)` : "";
                      })()
                    : ""
                }
                disabled
                helperText="Tự động tính từ Loại hội đồng và Vai trò của bạn"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Thông tin chi tiết"
                variant="outlined"
                value={values?.info || ""}
                multiline
                rows={3}
                onChange={(event) => handleChangeForm("info", event.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Ghi chú"
                variant="outlined"
                value={values?.note || ""}
                multiline
                rows={2}
                onChange={(event) => handleChangeForm("note", event.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values?.isOvertimeCountable || false}
                    disabled={props.role !== "sudo-admin"}
                    onChange={(event) => handleChangeForm("isOvertimeCountable", event.target.checked)}
                    color="primary"
                  />
                }
                label="Tính giờ ngoài quy chế"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Số giờ ngoài quy chế"
                variant="outlined"
                value={values?.overtimeHours ?? ""}
                disabled={props.role !== "sudo-admin" || !values?.isOvertimeCountable}
                onChange={(event) => handleChangeForm("overtimeHours", parseFloat(event.target.value) || 0)}
                inputProps={{
                  min: 0,
                  step: 0.1,
                }}
              />
            </Grid>

            <Grid item xs={5} style={{ margin: "auto" }}>
              <Divider />{" "}
            </Grid>
            <Grid item xs={2}>
              <Typography style={{ textAlign: "center", color: "rgba(0, 0, 0, 0.30)" }}>Thành viên</Typography>
            </Grid>
            <Grid item xs={5} style={{ margin: "auto" }}>
              <Divider />{" "}
            </Grid>

            <Grid item xs={12}>
              {profileList.map((e, index) => (
                <Grid key={`list-${index}`} container spacing={2} style={{ marginBottom: 8 }}>
                  <Grid item xs={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={e.isInternal !== false}
                          onChange={(event) => handleChangeProfileList(index, "isInternal", event.target.checked)}
                          color="primary"
                          disabled={index === 0}
                        />
                      }
                      label="Nội bộ"
                    />
                  </Grid>
                  {e.isInternal && (
                    <Grid item xs={5}>
                      <Autocomplete
                        disableClearable
                        disabled={index === 0}
                        options={props.profileList}
                        getOptionLabel={(option) => option.name}
                        value={profileListFromProps?.find((profile) => profile.id === e.profileId)}
                        onChange={(event, profile) => {
                          handleChangeProfileList(index, "profileId", parseInt(profile?.id));
                        }}
                        renderInput={(params) => (
                          <TextField {...params} size="small" label="Thành viên" variant="outlined" />
                        )}
                      />
                    </Grid>
                  )}
                  {!e.isInternal && (
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Tên thành viên"
                        value={e?.externalName || ""}
                        onChange={(event) => handleChangeProfileList(index, "externalName", event.target.value)}
                      />
                      {helperText && e.externalName === "" && (
                        <FormHelperText style={{ color: "red" }}>Không được để trống</FormHelperText>
                      )}
                    </Grid>
                  )}
                  <Grid item xs={5}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel id="role-label" style={{ padding: "0 8px", backgroundColor: "white" }}>
                        Vai trò
                      </InputLabel>
                      <Select
                        native
                        fullWidth
                        labelId="role-label"
                        value={e.roleInScientificCouncil}
                        onChange={(event) =>
                          handleChangeProfileList(index, "roleInScientificCouncil", event.target.value)
                        }
                      >
                        {SCIENTIFIC_COUNCIL_NEW.roleOptions.map((role) => (
                          <option key={role.key} value={role.value}>
                            {role.title}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={1}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="contained"
                      style={{ height: 40 }}
                      onClick={() => handleRemove(index)}
                    >
                      Xoá
                    </Button>
                  </Grid>
                </Grid>
              ))}
            </Grid>

            <Grid item xs={12}>
              <Button fullWidth variant="outlined" onClick={handleInsert}>
                Thêm thành viên
              </Button>
            </Grid>
          </Grid>
        </div>
        <div style={{ flex: "0 0 380px", paddingLeft: 16, borderLeft: "1px solid #e0e0e0" }}>
          <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
            File minh chứng
          </Typography>
          <MultiFileManager
            fileUrls={[]}
            setFileUrls={() => {}}
            newFiles={newFiles}
            setNewFiles={setNewFiles}
            disabled={false}
          />
        </div>
      </DialogContent>
      <DialogActions style={{ padding: "0 24px 8px 24px" }}>
        <Grid container justifyContent={"space-between"}>
          <div />
          <div style={{ float: "right" }}>
            <Button variant="outlined" color="secondary" onClick={handleCloseInsertModal} style={{ marginRight: 4 }}>
              Huỷ
            </Button>
            <Button variant="contained" color="primary" onClick={() => handleSubmit()}>
              Xác nhận
            </Button>
          </div>
        </Grid>
      </DialogActions>
      <DuplicateCheckModal
        open={duplicateModalOpen}
        handleClose={handleCloseDuplicateModal}
        handleSelect={handleSelectDuplicate}
        duplicateList={duplicateList}
      />
    </Dialog>
  );
}

const mapStateToProps = (state) => ({
  role: select(state, "userReducer", "role"),
  profile: select(state, "profileReducer", "detail"),
  profileList: select(state, "profileReducer", "list"),
  profileDepartments: select(state, "profileReducer", "departments"),
  departmentList: select(state, "departmentReducer", "departments"),
  listById: select(state, "scientificCouncilNewReducer", "listById"),
  timeResearchInformationList: select(state, "timeResearchInformationReducer", "list"),
});

const mapDispatchToProps = (dispatch) => ({
  statisticUnaccepted: (payload) => dispatch(statisticUnaccepted(payload)),
  insertScientificCouncil: (payload) => dispatch(insertScientificCouncil(payload)),
  uploadMultiFile: (id, files, type) => dispatch(uploadMultiFile(id, files, type)),
  updateScientificCouncil: (id, payload) => dispatch(updateScientificCouncil(id, payload)),
  checkDuplicateScientificCouncil: (payload) => dispatch(checkDuplicateScientificCouncil(payload)),
  pushNoti: (payload) => dispatch(notification(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(toJs(ScientificCouncilInsertModal));

